/** Player settings (docs/spec/04 §6, §7), kept in localStorage. */

import type { CrtMode, ScaleMode } from '../render/display/display';
import type { KeyValueStore } from './storage';
import { KEYS } from './storage';

export type Action = 'left' | 'right' | 'up' | 'down' | 'fire' | 'bomb' | 'pause';
export const ACTIONS: readonly Action[] = ['left', 'right', 'up', 'down', 'fire', 'bomb', 'pause'];

/** Two keys per action, as `KeyboardEvent.code`; '' is an empty slot. */
export type Bindings = Readonly<Record<Action, readonly [string, string]>>;

export const DEFAULT_BINDINGS: Bindings = {
  left: ['ArrowLeft', 'KeyA'],
  right: ['ArrowRight', 'KeyD'],
  up: ['ArrowUp', 'KeyW'],
  down: ['ArrowDown', 'KeyS'],
  fire: ['Space', 'KeyZ'],
  bomb: ['KeyX', 'ShiftLeft'],
  pause: ['Escape', 'KeyP'],
};

export type Preset = 'enhanced' | 'authentic' | 'custom';

export interface Options {
  readonly preset: Preset;
  readonly particles: boolean;
  readonly shake: boolean;
  /** The third, slow layer of stars and the occasional streak. */
  readonly deepStars: boolean;
  /** On a wide window, the instruction card and gauges either side of the screen. */
  readonly sidePanels: boolean;
  readonly crt: CrtMode;
  readonly scale: ScaleMode;
  /** 0-10. */
  readonly master: number;
  readonly sfx: number;
  readonly music: number;
  /** Holding fire pulses the button (an input aid, not a rule: README decision 7). */
  readonly turbo: boolean;
  readonly reduceFlashing: boolean;
  readonly touch: 'auto' | 'on' | 'off';
  readonly keys: Bindings;
}

type Look = Pick<Options, 'particles' | 'shake' | 'deepStars' | 'sidePanels' | 'crt'>;

const PRESETS: Record<'enhanced' | 'authentic', Look> = {
  enhanced: { particles: true, shake: true, deepStars: true, sidePanels: true, crt: 'soft' },
  authentic: { particles: false, shake: false, deepStars: false, sidePanels: false, crt: 'off' },
};

const LOOK_KEYS: readonly (keyof Look)[] = ['particles', 'shake', 'deepStars', 'sidePanels', 'crt'];

export const DEFAULT_OPTIONS: Options = {
  preset: 'enhanced',
  ...PRESETS.enhanced,
  scale: 'pixel',
  master: 8,
  sfx: 8,
  music: 6,
  turbo: false,
  reduceFlashing: false,
  touch: 'auto',
  keys: DEFAULT_BINDINGS,
};

/** Apply a preset's presentation settings. */
export function withPreset(o: Options, preset: Preset): Options {
  return preset === 'custom' ? { ...o, preset } : { ...o, ...PRESETS[preset], preset };
}

/** After changing a presentation setting by hand, the preset becomes Custom unless it still matches one. */
export function settlePreset(o: Options): Options {
  for (const name of ['enhanced', 'authentic'] as const) {
    const p = PRESETS[name];
    if (LOOK_KEYS.every((k) => p[k] === o[k])) return { ...o, preset: name };
  }
  return { ...o, preset: 'custom' };
}

function pick<T>(value: unknown, allowed: readonly T[], fallback: T): T {
  return allowed.includes(value as T) ? (value as T) : fallback;
}

function level(value: unknown, fallback: number): number {
  return typeof value === 'number' && Number.isFinite(value) ? Math.min(10, Math.max(0, Math.round(value))) : fallback;
}

function bindings(value: unknown): Bindings {
  const out: Record<Action, readonly [string, string]> = { ...DEFAULT_BINDINGS };
  if (typeof value !== 'object' || value === null) return out;
  for (const a of ACTIONS) {
    const v = (value as Record<string, unknown>)[a];
    if (Array.isArray(v) && v.length === 2 && v.every((k) => typeof k === 'string')) out[a] = [String(v[0]), String(v[1])];
  }
  return out;
}

export function parseOptions(json: string | null): Options {
  if (!json) return DEFAULT_OPTIONS;
  try {
    const r = JSON.parse(json) as Record<string, unknown>;
    const d = DEFAULT_OPTIONS;
    const bool = (k: keyof Options, f: boolean): boolean => (typeof r[k] === 'boolean' ? (r[k] as boolean) : f);
    return {
      preset: pick(r['preset'], ['enhanced', 'authentic', 'custom'] as const, d.preset),
      particles: bool('particles', d.particles),
      shake: bool('shake', d.shake),
      deepStars: bool('deepStars', d.deepStars),
      sidePanels: bool('sidePanels', d.sidePanels),
      crt: pick(r['crt'], ['off', 'soft', 'arcade'] as const, d.crt),
      scale: pick(r['scale'], ['pixel', 'fit'] as const, d.scale),
      master: level(r['master'], d.master),
      sfx: level(r['sfx'], d.sfx),
      music: level(r['music'], d.music),
      turbo: bool('turbo', d.turbo),
      reduceFlashing: bool('reduceFlashing', d.reduceFlashing),
      touch: pick(r['touch'], ['auto', 'on', 'off'] as const, d.touch),
      keys: bindings(r['keys']),
    };
  } catch {
    return DEFAULT_OPTIONS;
  }
}

/** The live settings: read them, change them, hear about changes. */
export class OptionsStore {
  private value: Options;
  private readonly listeners = new Set<(o: Options) => void>();

  constructor(private readonly store: KeyValueStore) {
    this.value = parseOptions(store.load(KEYS.options));
  }

  get(): Options {
    return this.value;
  }

  set(next: Options): void {
    this.value = next;
    this.store.save(KEYS.options, JSON.stringify(next));
    for (const l of this.listeners) l(next);
  }

  update(change: Partial<Options>): void {
    this.set({ ...this.value, ...change });
  }

  /** Apply for this page load only (URL overrides such as `?fx=authentic`), without saving. */
  override(change: Partial<Options>): void {
    this.value = { ...this.value, ...change };
    for (const l of this.listeners) l(this.value);
  }

  subscribe(listener: (o: Options) => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }
}
